import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface Permission {
  id: string;
  name: string;
  module: string;
  action: string;
  slug: string;
}

interface RolePermissionsSelectorProps {
  permissions: Permission[];
  selectedPermissionIds: string[];
  onPermissionsChange: (permissionIds: string[]) => void;
}

export function RolePermissionsSelector({
  permissions,
  selectedPermissionIds,
  onPermissionsChange,
}: RolePermissionsSelectorProps) {
  // Agrupar permissions por módulo
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.module]) {
      acc[permission.module] = [];
    }
    acc[permission.module].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  const modules = Object.keys(groupedPermissions).sort();

  // Estado de expansão dos módulos (todos abertos por padrão)
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>(
    modules.reduce((acc, module) => ({ ...acc, [module]: true }), {})
  );

  const toggleModule = (module: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [module]: !prev[module],
    }));
  };

  const isModuleFullySelected = (module: string) => {
    const modulePerms = groupedPermissions[module];
    return modulePerms.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isModulePartiallySelected = (module: string) => {
    const modulePerms = groupedPermissions[module];
    const selectedCount = modulePerms.filter((p) => selectedPermissionIds.includes(p.id)).length;
    return selectedCount > 0 && selectedCount < modulePerms.length;
  };

  const toggleModulePermissions = (module: string) => {
    const modulePerms = groupedPermissions[module];
    const allSelected = isModuleFullySelected(module);

    if (allSelected) {
      // Desmarcar todas do módulo
      const newSelected = selectedPermissionIds.filter(
        (id) => !modulePerms.some((p) => p.id === id)
      );
      onPermissionsChange(newSelected);
    } else {
      // Marcar todas do módulo
      const moduleIds = modulePerms.map((p) => p.id);
      const newSelected = [...new Set([...selectedPermissionIds, ...moduleIds])];
      onPermissionsChange(newSelected);
    }
  };

  const togglePermission = (permissionId: string) => {
    if (selectedPermissionIds.includes(permissionId)) {
      onPermissionsChange(selectedPermissionIds.filter((id) => id !== permissionId));
    } else {
      onPermissionsChange([...selectedPermissionIds, permissionId]);
    }
  };

  const getModuleLabel = (module: string) => {
    const labels: Record<string, string> = {
      products: "Produtos",
      clients: "Clientes",
      suppliers: "Fornecedores",
      transactions: "Transações",
      users: "Usuários",
      roles: "Perfis",
      permissions: "Permissões",
      reports: "Relatórios",
      dashboard: "Dashboard",
      settings: "Configurações",
      tenants: "Tenants",
    };
    return labels[module] || module;
  };

  if (permissions.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Nenhuma permissão disponível
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto border rounded-md p-4">
      {modules.map((module) => {
        const isExpanded = expandedModules[module];
        const isFullySelected = isModuleFullySelected(module);
        const isPartiallySelected = isModulePartiallySelected(module);

        return (
          <Collapsible
            key={module}
            open={isExpanded}
            onOpenChange={() => toggleModule(module)}
          >
            <div className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded-md">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-0 h-auto">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              
              <Checkbox
                id={`module-${module}`}
                checked={isFullySelected}
                onCheckedChange={() => toggleModulePermissions(module)}
                className={isPartiallySelected ? "data-[state=indeterminate]:bg-primary" : ""}
              />
              
              <Label
                htmlFor={`module-${module}`}
                className="flex-1 font-semibold cursor-pointer"
              >
                {getModuleLabel(module)}
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({groupedPermissions[module].filter((p) => selectedPermissionIds.includes(p.id)).length}/
                  {groupedPermissions[module].length})
                </span>
              </Label>
            </div>

            <CollapsibleContent>
              <div className="ml-8 space-y-2 mt-2">
                {groupedPermissions[module].map((permission) => (
                  <div key={permission.id} className="flex items-center gap-2">
                    <Checkbox
                      id={permission.id}
                      checked={selectedPermissionIds.includes(permission.id)}
                      onCheckedChange={() => togglePermission(permission.id)}
                    />
                    <Label
                      htmlFor={permission.id}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {permission.name}
                      <span className="ml-2 text-xs text-muted-foreground">
                        ({permission.action})
                      </span>
                    </Label>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
